import { RegionsCode } from '../../constants/region';
import { deepTransformDate, deepTransformTimestamp } from '../../utils/date';
import {
  AttachmentAudio,
  AttachmentImage,
  AttachmentVideo,
  CarouselItem,
  UserId,
  Uuid,
} from '../Base';
import { RepositoryElement } from '../Repository';

// Define the basic types
export type NameTypes = 'name';
export type TextTypes =
  | 'title'
  | 'department'
  | 'company'
  | 'description'
  | 'note';

export enum PhoneSubType {
  Mobile = 'mobile',
  Home = 'home',
  Work = 'work',
  School = 'school',
  HomeFax = 'homeFax',
  WorkFax = 'workFax',
  Pager = 'pager',
  Other = 'other',
  Custom = 'custom',
}

export enum AddressSubType {
  Home = 'home',
  Work = 'work',
  School = 'school',
  Other = 'other',
  Custom = 'custom',
}

export enum WebsiteSubType {
  Homepage = 'homepage',
  Home = 'home',
  Work = 'work',
  School = 'school',
  Other = 'other',
  Custom = 'custom',
}

export enum ImportantDateSubType {
  Birthday = 'birthday',
  Anniversary = 'anniversary',
  Other = 'other',
  Custom = 'custom',
}

export enum SocialProfileSubType {
  JOYAJOY = 'joyajoy',
  Facebook = 'facebook',
  Instagram = 'instagram',
  Youtube = 'youtube',
  Linkedin = 'linkedin',
  SinaWeibo = 'weibo',
  Custom = 'custom',
}

export enum InstantMessageSubType {
  WhatsApp = 'whatsapp',
  Skype = 'skype',
  Facebook = 'facebook',
  Line = 'line',
  WeChat = 'wechat',
  GoogleChat = 'googleChat',
  QQ = 'QQ',
  ICQ = 'ICQ',
  Jabber = 'jabber',
  Custom = 'custom',
}

interface NameText {
  type: NameTypes;
  text: string;
  otherText?: string;
}
interface ShortText {
  type: TextTypes;
  text: string;
}

interface Phones {
  type: 'phone'; // may add a new type "PhoneTypes"
  subtype: PhoneSubType;
  region: RegionsCode; // Region
  phoneNumber: string;
  customText?: string;
}

interface Address {
  type: 'email' | 'address';
  subtype: AddressSubType;
  address: string;
  customText?: string;
}

interface Website {
  type: 'website';
  subtype: WebsiteSubType;
  website: string;
  customText?: string;
}

interface ImportantDate {
  type: 'importantDate';
  subtype: ImportantDateSubType;
  importantDate: string;
  customText?: string;
}

interface SocialProfile {
  type: 'socialProfile';
  subtype: SocialProfileSubType;
  account: string;
  customText?: string;
}

interface InstantMessage {
  type: 'instantMessage';
  subtype: InstantMessageSubType;
  account: string;
  customText?: string;
}

interface ProfilePicture {
  type: 'profilePicture';
  use: 'accountAvatar' | 'accountProfilePicture' | 'classroomIcon'; // for the acutal value, you need get it from profile or classroomIcon
}

interface Banner {
  type: 'banner';
  use: 'accountBanner' | 'classroomCarouselImage';
}

interface Image {
  type: 'image' | 'banner' | 'profilePicture';
  image: AttachmentImage;
}

interface Video {
  type: 'video';
  video: AttachmentVideo;
}

interface Audio {
  type: 'audio';
  audio: AttachmentAudio;
}

interface Carousel {
  type: 'carousel';
  carousel: CarouselItem;
}

// SectionItem can be either Types
export type SectionItem =
  | NameText
  | ShortText
  | Phones
  | Address
  | Website
  | ImportantDate
  | SocialProfile
  | InstantMessage
  | ProfilePicture
  | Banner
  | Image
  | Video
  | Audio
  | Carousel;

// Section can be either SectionItem or an array of Section
export type Section = SectionItem | Section[];

// Pages is an array of Section
export type Pages = Section[];

export enum CardType {
  Primary = 'primary',
  Secondary = 'secondary',
  Other = 'other',
  Organization = 'organization',
}

export enum Audience {
  Public = 'public',
  Followers = 'followers',
}

export interface Style {
  template: number;
  qrcodeTemplate: number;
  primaryColor: string;
  textColor: string;
  secondaryColor: string;
  qrCodeColor: string;
  fontStyle: string;
  fontSize: number;
}

interface GameSet {
  gameSetId: Uuid;
  isShown: boolean;
}

interface Transfer<T = number> {
  invitationLinkId: string;
  transferSentAt: T;
}

interface Statistics {
  collectCount?: number;
}

export interface NameCardJson<T = number> {
  nameCardId: Uuid;
  userId: UserId;
  createdAt: T;
  lastModifiedAt: T;
  cardType: CardType;
  pages: Pages;
  style: Style;
  audience: Audience;
  gameSet: GameSet | null;
  transfer: Transfer<T> | null;
  statistics: Statistics;
  classroomId?: UserId;
}

export class NameCard extends RepositoryElement implements NameCardJson<Date> {
  nameCardId!: Uuid;
  userId!: UserId;
  createdAt!: Date;
  lastModifiedAt!: Date;
  cardType!: CardType;
  pages!: Pages;
  style!: Style;
  audience!: Audience;
  gameSet!: GameSet | null;
  transfer!: Transfer<Date> | null;
  statistics!: Statistics;
  classroomId?: UserId;

  constructor(nameCard: NameCardJson<Date | number>) {
    super();
    Object.assign(this, deepTransformTimestamp(nameCard));
  }

  id() {
    return this.nameCardId;
  }

  toJSON(): NameCardJson {
    return deepTransformDate(this) as unknown as NameCardJson;
  }
}

export interface CollectedNameCardJson<T = number> {
  nameCardId: Uuid;
  userId: UserId;
  collectedAt: T;
}

export class CollectedNameCard
  extends RepositoryElement
  implements CollectedNameCardJson<Date>
{
  nameCardId!: Uuid;
  userId!: UserId;
  collectedAt!: Date;

  constructor(collectedNameCard: CollectedNameCardJson<Date | number>) {
    super();
    Object.assign(this, deepTransformTimestamp(collectedNameCard));
  }

  id() {
    return this.nameCardId; // may need to change to userId + nameCardId
  }

  toJSON(): CollectedNameCardJson {
    return deepTransformDate(this) as unknown as CollectedNameCardJson;
  }
}
